import type { VariableDef } from "@/components/variable-input";
import type { FormField } from "@/hooks/schema/use-schema";
import { cleanupEntityName } from "@/lib/utils";

/**
 * Drops the JSONPath root so a hand-written expression (`$.node.items[*].id`)
 * walks the variable tree like the dotted form the pickers emit.
 */
export const stripJsonPathRoot = (path: string): string => path.trim().replace(/^\$\.?/, "");

/**
 * Maps a raw type name (entity `data_type`, an action output `data_type`, or a
 * parsed TS type name) onto the canonical field-type enum the filter operator
 * catalog (`availableFilterOperators`) keys off via `activeFor`. Returns
 * undefined for anything unrecognised so callers fall back to a plain text input.
 */
export const normalizeDataType = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  const t = raw
    .replace(/(\[\])+$/, "")
    .trim()
    .toUpperCase();
  const map: Record<string, string> = {
    STRING: "STRING",
    TEXT: "TEXT",
    LONGTEXT: "LONGTEXT",
    RICHTEXT: "RICHTEXT",
    ID: "ID",
    UUID: "ID",
    INT: "INT",
    INTEGER: "INT",
    NUMBER: "FLOAT",
    FLOAT: "FLOAT",
    DOUBLE: "FLOAT",
    DECIMAL: "FLOAT",
    BOOL: "BOOLEAN",
    BOOLEAN: "BOOLEAN",
    DATE: "DATE",
    DATETIME: "DATETIME",
    TIMESTAMP: "DATETIME",
    TIME: "TIME",
    ENUM: "ENUM",
    JSON: "JSON",
  };
  return map[t];
};

/**
 * An entity's schema fields as picker variables. Scalars are leaves; relations
 * (OBJECT) are drillable so a path can go deeper (e.g.
 * `me.pylo_user.pylo_customer.name`). Variant fields are noise.
 *
 * `scalarOnly` drops relations entirely, for sources whose payload never carries
 * them — see `VariableDef.scalarFieldsOnly`.
 */
export const entityFieldsAsVariables = (
  fields: FormField[] | undefined,
  { scalarOnly = false, search = "" }: { scalarOnly?: boolean; search?: string } = {},
): VariableDef[] =>
  (fields ?? [])
    .filter(
      (field) =>
        field.kind === "SCALAR" ||
        field.kind === "ENUM" ||
        (!scalarOnly && field.kind === "OBJECT" && field.type !== "FieldVariantsData"),
    )
    .filter((field) => field.name.toLowerCase().includes(search.toLowerCase()))
    .map((field) => ({
      name: field.name,
      displayName: field.name,
      description: field.description ?? "",
      ...(field.kind === "OBJECT" && field.type
        ? { entityName: cleanupEntityName(field.type) }
        : {}),
    }));

export interface ResolvedFieldMeta {
  /** Canonical field type when the path resolves to a typed tree leaf. */
  dataType?: string;
  isArray?: boolean;
  /** Set when the path drills into an entity; the field type is then looked up from that entity's schema. */
  entityName?: string;
  entityFieldName?: string;
  /** Target entity of a relation leaf, so values can be picked as records instead of ids. */
  relationEntityName?: string;
}

/**
 * Walks a dot-path against the variable tree to determine the selected field's
 * type. A path either bottoms out at a typed leaf (action outputs, parsed
 * fields) or crosses an entity boundary (`entityName`), in which case the caller
 * resolves the remaining single field from that entity's schema.
 *
 * Matches segments by prefix rather than a naive split, so leaf names that
 * themselves contain dots (e.g. `connected.id`) resolve correctly.
 */
export const resolveVariableFieldMeta = (
  path: string,
  variables: VariableDef[],
): ResolvedFieldMeta => {
  const remaining = stripJsonPathRoot(
    path
      .replace(/^\$\{/, "")
      .replace(/\}$/, "")
      .replace(/\[[^\]]*\]/g, ""),
  );
  if (!remaining) return {};

  const walk = (vars: VariableDef[] | undefined, rest: string): ResolvedFieldMeta => {
    for (const v of vars ?? []) {
      const name = v.name.replace(/\[[^\]]*\]/g, "");
      if (rest === name) {
        return v.entityName
          ? { entityName: v.entityName, isArray: v.isArray }
          : {
              dataType: v.dataType,
              isArray: v.isArray,
              ...(v.relationEntityName ? { relationEntityName: v.relationEntityName } : {}),
              ...(v.sourceEntityName && v.sourceFieldName
                ? { entityName: v.sourceEntityName, entityFieldName: v.sourceFieldName }
                : {}),
            };
      }
      if (rest.startsWith(name + ".")) {
        const tail = rest.slice(name.length + 1);
        if (v.entityName) {
          // A single remaining segment is a scalar field of that entity.
          return {
            entityName: v.entityName,
            entityFieldName: tail.includes(".") ? undefined : tail,
            isArray: v.isArray,
          };
        }
        if (v.children?.length) return walk(v.children, tail);
        return {};
      }
    }
    return {};
  };

  return walk(variables, remaining);
};

const sampleDataType = (value: unknown): string | undefined => {
  if (typeof value === "string") return "STRING";
  if (typeof value === "number") return Number.isInteger(value) ? "INT" : "FLOAT";
  if (typeof value === "boolean") return "BOOLEAN";
  return undefined;
};

/**
 * Derives a variable tree from a sample value — used for an iterator whose source
 * is a literal JSON array (`[{"name":"a","value":1}]`) rather than a `${...}`
 * reference. There is no declared type to inherit from, so the first element's
 * shape stands in for every item's.
 */
export const inferVariablesFromSample = (sample: unknown): VariableDef[] | undefined => {
  if (!sample || typeof sample !== "object" || Array.isArray(sample)) return undefined;

  return Object.entries(sample as Record<string, unknown>).map(([key, value]) => {
    const isArray = Array.isArray(value);
    const element = isArray ? (value as unknown[])[0] : value;
    const children = inferVariablesFromSample(element);
    const dataType = sampleDataType(element);
    return {
      name: key,
      displayName: key,
      description: `Field of each item (${isArray ? "array" : (dataType ?? "object")})`,
      ...(isArray ? { isArray: true } : {}),
      ...(dataType ? { dataType } : {}),
      ...(children?.length ? { children } : {}),
    };
  });
};

/**
 * Replaces every `${...}` sitting outside a JSON string with `null` so a literal
 * carrying placeholders still parses. Mirrors flowservice, which interpolates
 * before `JSON.parse` — at design time there is no value, so the field is kept
 * (it shows up in the picker) but stays untyped.
 */
const neutralizePlaceholders = (template: string): string => {
  let out = "";
  let inString = false;

  for (let i = 0; i < template.length; i++) {
    const char = template[i];
    if (inString && char === "\\") {
      out += char + (template[i + 1] ?? "");
      i++;
      continue;
    }
    if (char === '"') {
      inString = !inString;
      out += char;
      continue;
    }
    if (!inString && char === "$" && template[i + 1] === "{") {
      const end = template.indexOf("}", i + 2);
      if (end > i + 1) {
        out += "null";
        i = end;
        continue;
      }
    }
    out += char;
  }

  return out;
};

/**
 * Item fields for an iterator whose `source_expression` is a literal JSON array.
 * Returns undefined for a `${...}` reference (that resolves against the variable
 * tree instead) or for anything that still isn't parseable.
 */
export const inferItemVariablesFromLiteral = (expression?: string): VariableDef[] | undefined => {
  const trimmed = expression?.trim();
  if (!trimmed || !trimmed.startsWith("[")) return undefined;
  try {
    const parsed: unknown = JSON.parse(neutralizePlaceholders(trimmed));
    return Array.isArray(parsed) ? inferVariablesFromSample(parsed[0]) : undefined;
  } catch {
    return undefined;
  }
};
